// PropertyEditor.jsx
import React, { useState, useEffect } from 'react';
import IncomingConnectionsList from './IncomingConnectionsList.jsx';
import API from '../../api/API.jsx';
import './PropertyEditor.css';

/** Il lato in pixel della miniatura del logo: nel pannello si vede piccolo. */
const MINIATURA = 40;

const DEFAULT_SORT_PRIORITY = ['punti', 'diff mete', 'piu pieni', 'meno falli', 'meno vuoti'];
const SORT_PRIORITY_LABELS = {
  'punti': 'Punti',
  'diff mete': 'Differenza mete',
  'piu pieni': 'Più pieni',
  'meno falli': 'Meno falli',
  'meno vuoti': 'Meno vuoti'
};

const PropertyEditor = ({ node, onUpdate, onClose, connections = [], nodes = [], onUpdateConnectionNumber, onDeleteConnection, cachedTeams = null, onLoadTeams = null }) => {
  const [editedNode, setEditedNode] = useState({ ...node });
  const [teams, setTeams] = useState(cachedTeams || []);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setEditedNode({ ...node });
    
    // Carica le squadre solo se stiamo editando un nodo squadra
    if (node.type === 'team') {
      if (cachedTeams && cachedTeams.length > 0) {
        // Usa la cache se disponibile
        setTeams(cachedTeams);
        setFilteredTeams(cachedTeams);
      } else {
        // Carica solo se non abbiamo cache
        loadTeams();
      }
    }
  }, [node, cachedTeams]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const edizione = JSON.parse(localStorage.getItem('selectedEdition') || '{}');
      
      // Solo id e nome: della tendina si legge il nome e si salva l'id, e il logo lo chiede il
      // browser come immagine. Prima qui arrivava l'edizione intera, loghi in base64 compresi.
      const response = await API.accettazione.elenco(edizione.id);
      if (response.isSuccess && response.data) {
        setTeams(response.data);
        setFilteredTeams(response.data);
        
        // Notifica il componente padre per aggiornare la cache
        if (onLoadTeams) {
          onLoadTeams(response.data);
        }
      } else {
        console.error('Errore nel caricamento delle squadre:', response.errorMsg);
      }
    } catch (error) {
      console.error('Errore nella chiamata API per le squadre:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.autocomplete-container')) {
        setDropdownOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Filtra le squadre in base al testo di ricerca
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredTeams(teams);
    } else {
      const filtered = teams.filter(team => 
        team.nome?.toLowerCase().includes(searchText.toLowerCase()) ||
        team.id.toString().includes(searchText)
      );
      setFilteredTeams(filtered);
    }
  }, [searchText, teams]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    setDropdownOpen(true);
  };

  const updateDropdownPosition = (inputElement) => {
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  };

  const handleTeamSelect = (team) => {
    handleInputChange('idSquadra', team.id);
    //handleInputChange('teamId', team.id);
    handleInputChange('name', team.nome || `Team ${team.id}`);
    // il logo non si copia nel nodo: si ricava dall'id quando serve disegnarlo, e la formula non
    // deve portarsi dietro un'immagine in base64
    setSearchText('');
    setDropdownOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setDropdownOpen(false);
      setSearchText('');
    } else if (e.key === 'Enter' && filteredTeams.length === 1) {
      e.preventDefault();
      handleTeamSelect(filteredTeams[0]);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedNode(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onUpdate(node.id, editedNode);
    onClose();
  };

  const handleUpdateConnectionOrder = (nodeId, newOrderMap) => {
    const updatedNode = {
      ...editedNode,
      incomingConnectionsOrder: newOrderMap
    };
    setEditedNode(updatedNode);
    // Aggiorna immediatamente anche il nodo padre
    onUpdate(nodeId, updatedNode);
  };

  const currentSortPriority = (() => {
    const sp = editedNode.sortPriority;
    if (!Array.isArray(sp) || sp.length === 0) return [...DEFAULT_SORT_PRIORITY];
    const valid = sp.filter(k => DEFAULT_SORT_PRIORITY.includes(k));
    const missing = DEFAULT_SORT_PRIORITY.filter(k => !valid.includes(k));
    return [...valid, ...missing];
  })();

  const moveSortPriority = (index, delta) => {
    const next = [...currentSortPriority];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    handleInputChange('sortPriority', next);
  };

  const resetSortPriority = () => {
    handleInputChange('sortPriority', null);
  };

  const handleUpdateConnectionNumber = (connectionId, newNumber) => {
    // Trova e aggiorna la connessione corrispondente
    const updatedConnections = connections.map(conn => 
      conn.id === connectionId 
        ? { ...conn, connectionNumber: newNumber }
        : conn
    );
    
    // Notifica il componente padre del cambiamento
    if (onUpdateConnectionNumber) {
      onUpdateConnectionNumber(connectionId, newNumber);
    }
  };

  return (
    <div className="property-editor">
      <div className="property-editor-header">
        <h3>Modifica {node.type === 'team' ? 'Squadra' : 'Fase'}</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="property-editor-content">
        {/* Nome o Selezione Squadra */}
        <div className="property-group">
          {node.type === 'team' ? (
            <>
              <label>Squadra:</label>
              {loading ? (
                <div>Caricamento squadre...</div>
              ) : (
                <div className="autocomplete-container">
                  {/* Squadra selezionata */}
                  {editedNode.idSquadra && !dropdownOpen ? (
                    <div 
                      className="selected-team"
                      onClick={(e) => {
                        setDropdownOpen(true);
                        setSearchText('');
                        updateDropdownPosition(e.target);
                      }}
                    >
                      <div className="team-option">
                        <img
                          src={API.squadra.logoUrl(editedNode.idSquadra, MINIATURA)}
                          alt={`Logo ${editedNode.name ?? ''}`}
                          className="team-logo-small"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="team-name">{editedNode.name}</span>
                      </div>
                      <span className="edit-icon">✏️</span>
                    </div>
                  ) : (
                    /* Input di ricerca */
                    <input
                      type="text"
                      className="autocomplete-input"
                      placeholder="Scrivi per cercare una squadra..."
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={(e) => {
                        setDropdownOpen(true);
                        updateDropdownPosition(e.target);
                      }}
                      onKeyDown={handleKeyDown}
                      autoComplete="off"
                    />
                  )}
                  
                  {/* Lista opzioni filtrate */}
                  {dropdownOpen && filteredTeams.length > 0 && (
                    <div 
                      className="autocomplete-options"
                      style={{
                        width: dropdownPosition.width
                      }}
                    >
                      {filteredTeams.map(team => (
                        <div 
                          key={team.id} 
                          className="dropdown-option"
                          onClick={() => handleTeamSelect(team)}
                        >
                          <img
                            src={API.squadra.logoUrl(team.id, MINIATURA)}
                            alt={`Logo ${team.nome ?? ''}`}
                            className="team-logo-small"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <span className="team-name">{team.nome || `Team ${team.id}`}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Messaggio nessun risultato */}
                  {dropdownOpen && searchText && filteredTeams.length === 0 && (
                    <div className="no-results">
                      Nessuna squadra trovata per "{searchText}"
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <label>Nome:</label>
              <input
                type="text"
                value={editedNode.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </>
          )}
        </div>

        {/* ID squadra viene impostato automaticamente dalla selezione */}
        {node.type === 'team' && editedNode.idSquadra && (
          <div className="property-group">
            <label>ID Squadra:</label>
            <div className="readonly-field">{editedNode.idSquadra}</div>
          </div>
        )}

        {/* Sezione priorità ordinamento - solo per nodi Classifica */}
        {node.type !== 'team' && node.phaseType === 'Classifica' && (
          <div className="property-group">
            <label>Priorità ordinamento squadre:</label>
            <ol className="sort-priority-list">
              {currentSortPriority.map((key, index) => (
                <li key={key} className="sort-priority-item">
                  <span className="sort-priority-rank">{index + 1}.</span>
                  <span className="sort-priority-label">{SORT_PRIORITY_LABELS[key] || key}</span>
                  <div className="sort-priority-actions">
                    <button
                      type="button"
                      className="sort-priority-btn"
                      onClick={() => moveSortPriority(index, -1)}
                      disabled={index === 0}
                      title="Sposta su"
                    >▲</button>
                    <button
                      type="button"
                      className="sort-priority-btn"
                      onClick={() => moveSortPriority(index, 1)}
                      disabled={index === currentSortPriority.length - 1}
                      title="Sposta giù"
                    >▼</button>
                  </div>
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="sort-priority-reset"
              onClick={resetSortPriority}
              title="Ripristina ordine predefinito"
            >
              Ripristina predefinito
            </button>
          </div>
        )}

        {/* Sezione connessioni in ingresso - solo per nodi fase, non per squadre */}
        {node.type !== 'team' && (
          <IncomingConnectionsList
            node={editedNode}
            connections={connections}
            nodes={nodes}
            onUpdateConnectionOrder={handleUpdateConnectionOrder}
            onUpdateConnectionNumber={handleUpdateConnectionNumber}
            onDeleteConnection={onDeleteConnection}
          />
        )}
      </div>

      <div className="property-editor-footer">
        <button className="cancel-btn" onClick={onClose}>Annulla</button>
        <button className="save-btn" onClick={handleSave}>Salva</button>
      </div>
    </div>
  );
};

export default PropertyEditor;